/**
 * Pause all missions (ADR 0103): one switch, on the Missions page header and in the command
 * menu. Paused, Orgus starts nothing new (Autopilot, the queue, sequences, Watch fixes), queued
 * work waits and no mission session is woken; running sessions finish their current turn and
 * wait. Nothing is stopped. Resume lets it all go on.
 */
import { Pause, Play } from '@orgus/ui/icons';
import { useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import type { MissionPause } from '@orgus/protocol';
import { Banner, Button, RelativeTime, toUiError } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { useRegisterCommands } from '../../lib/commands.ts';
import type { Command } from '../../lib/commands.ts';
import { useOrgus } from '../../lib/store.ts';
import { useCan } from '../auth/useMe.ts';
import { setMissionsPaused } from './data.ts';
import { missionsSlice } from './model.ts';

/** The global pause from the missions slice; null while not paused, undefined before the slice loads. */
export function useMissionsPause(): MissionPause | null | undefined {
  return useOrgus(useShallow((s) => {
    const slice = missionsSlice(s.ext);
    return slice ? (slice.pause ?? null) : undefined;
  }));
}

function usePauseToggle(): { paused: MissionPause | null | undefined; busy: boolean; error: UiError | null; toggle: () => Promise<void> } {
  const paused = useMissionsPause();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const toggle = async () => {
    setBusy(true);
    setError(null);
    try {
      await setMissionsPaused(!paused);
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };
  return { paused, busy, error, toggle };
}

/** The header control: Pause missions, or Resume missions while paused. Operators only. */
export function PauseMissionsButton() {
  const can = useCan('operator');
  const { paused, busy, error, toggle } = usePauseToggle();
  if (!can || paused === undefined) return null;
  return (
    <Button
      variant="secondary"
      size="sm"
      icon={paused ? <Play /> : <Pause />}
      loading={busy}
      onClick={() => void toggle()}
      title={error ? `${error.what} ${error.why}` : paused ? 'Let queued work start again' : 'Nothing new starts; running sessions finish their turn and wait'}
    >
      {paused ? 'Resume missions' : 'Pause missions'}
    </Button>
  );
}

/** While paused: what holds and what still runs; the header's Resume missions undoes it. */
export function PausedBanner() {
  const paused = useMissionsPause();
  if (!paused) return null;
  return (
    <Banner icon={<Pause />} title="Missions are paused" className="mb-3">
      Nothing new starts and queued work waits. Running sessions finish their current turn and wait. Paused by {paused.by} <RelativeTime ts={paused.at} />
    </Banner>
  );
}

/** Pause all missions and Resume missions in the command menu, on every page. */
export function useMissionsPauseCommands(): void {
  const can = useCan('operator');
  const paused = useMissionsPause();
  const cmds = useMemo<Command[]>(() => {
    if (!can || paused === undefined) return [];
    return [
      paused
        ? { id: 'missions.resume', title: 'Resume missions', subtitle: 'Queued work starts again', group: 'Missions', icon: Play, keywords: ['pause', 'resume', 'autopilot', 'stop'], perform: () => void setMissionsPaused(false) }
        : { id: 'missions.pause', title: 'Pause all missions', subtitle: 'Nothing new starts; running sessions finish their turn', group: 'Missions', icon: Pause, keywords: ['pause', 'stop', 'hold', 'autopilot', 'freeze'], perform: () => void setMissionsPaused(true) },
    ];
  }, [can, paused]);
  useRegisterCommands(cmds, [cmds]);
}
